// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import RuleIcon from '@mui/icons-material/Rule'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import VpnLockIcon from '@mui/icons-material/VpnLock'
import CampaignIcon from '@mui/icons-material/Campaign'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function WhyMattersSection() {
  const { t } = useTranslation('servicesOtAwarenessTraining')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const items: { icon: ReactElement; title: string; text: string }[] = [
    {
      icon: <RuleIcon sx={iconSx} />,
      title: t('why.items.01.title'),
      text: t('why.items.01.text')
    },
    {
      icon: <PrecisionManufacturingIcon sx={iconSx} />,
      title: t('why.items.02.title'),
      text: t('why.items.02.text')
    },
    {
      icon: <VpnLockIcon sx={iconSx} />,
      title: t('why.items.03.title'),
      text: t('why.items.03.text')
    },
    {
      icon: <CampaignIcon sx={iconSx} />,
      title: t('why.items.04.title'),
      text: t('why.items.04.text')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('why.eyebrow')}
        title={t('why.title')}
        intro={[
          t('why.intro1'),
          t('why.intro2'),
          t('why.intro3'),
          t('why.intro4')
        ]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {items.map(item => (
          <Box
            key={item.title}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1.25rem',
              height: '100%',
              background: themeColors.kroemtech.background,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.75rem',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ flexShrink: 0, lineHeight: 0, marginTop: '0.15rem' }}>
              {item.icon}
            </Box>
            <Box>
              <Typography
                variant="h3"
                component="h3"
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: themeColors.kroemtech.dunkelblau,
                  marginBottom: '0.6rem'
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: getFontBodySmall(),
                  lineHeight: 1.65,
                  color: themeColors.gray.defaultText
                }}
              >
                {item.text}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
